import { useState } from 'react';
import useFormValidation from '../../../../../hooks/useFormValidation';
import useResponsibleFieldsConfig from './useResponsibleFieldsConfig';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../../../hooks/useTranslate';
import useAlerts from './useAlerts';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/reduxHooks';
import { enqueueSnackbar } from 'notistack';
import { showAlert } from '../../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../../constants/values';
import useResponsibles from './useResponsibles';
import { CUSTOM_HABILITATION_TITLE_OPTION, isExisitingHabilitationTitle } from '../../../../../commons/presentations';
import { debeAvisarResponsableDuplicado } from '../helpers';

export interface IResponsibleForm {
    id: string;
    name: string;
    surname: string;
    cuit: string;
    charge: string;
    respresentation: string;
    status: string;
    habilitationTitle: string;
    licenceNumber: string;
    professionalType: string;
    grantingEntity: string;
    hoursQuantity: string;
    otherHabilitationTitle?: string;
}

const initialResponsibleForm = {
    id: '',
    name: '',
    surname: '',
    cuit: '',
    charge: '',
    respresentation: '',
    status: '',
    habilitationTitle: '',
    otherHabilitationTitle: '',
    licenceNumber: '',
    grantingEntity: '',
    professionalType: '',
    hoursQuantity: ''
};

const getInitialData = ({ selectedResponsible, isEditing }: any) => {
    if (!isEditing) return initialResponsibleForm;
    return {
        id: selectedResponsible?.id || '',
        name: selectedResponsible?.nombre || '',
        surname: selectedResponsible?.apellido || '',
        cuit: selectedResponsible?.cuit || '',
        charge: selectedResponsible?.idCargo || '',
        respresentation: selectedResponsible?.idRepresentacion || '',
        status: selectedResponsible?.idTipoContratacion || '',
        habilitationTitle: isExisitingHabilitationTitle(selectedResponsible?.titulo) ? selectedResponsible?.titulo : CUSTOM_HABILITATION_TITLE_OPTION,
        licenceNumber: selectedResponsible?.matricula || '',
        grantingEntity: selectedResponsible?.entidadOtorgante || '',
        professionalType: selectedResponsible?.idTipoProfesional || '',
        hoursQuantity: selectedResponsible?.cantidadHoras || '',
        otherHabilitationTitle: isExisitingHabilitationTitle(selectedResponsible?.titulo) ? '' : selectedResponsible?.titulo || ''
    };
};

export const useResponsibleForm = ({ isEditing, selectedResponsible, handleCloseDrawer }: any) => {
    const dispatch = useAppDispatch();
    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );

    const initialData = getInitialData({ selectedResponsible, isEditing });
    const [formData, setFormData] = useState<IResponsibleForm>(initialData);
    const { responsibles, addResponsible, editResponsible, isAddingResponsible, isEditingResponsible } =
        useResponsibles();
    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.responsiblesAndContractors'
    });

    const { translate } = useTranslate(t);

    const {
        errors,
        hasValidationErrors,
        validateForm,
        handleBlurValidation,
        resetValidation,
        setErrors
    } = useFormValidation();

    const { responsibleFieldsConfig } = useResponsibleFieldsConfig({
        t,
        formData,
        handleBlurValidation
    });

    const { showConfirmCreateResponsibleAlert, showConfirmEditResponsibleAlert } = useAlerts({
        translate: t
    });

    const handleChange = (name: string, value: any) => {
        setErrors((prevState) => ({
            ...prevState,
            [name]: { hasError: false, errorMessage: '' }
        }));
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleEditNewResponsible = async () => {
        try {
            if (!selectedCarcinogenicReportId || !formData.id) throw new Error('No report selected');

            const adaptedBody = {
                nombre: formData.name,
                apellido: formData.surname,
                cuit: formData.cuit,
                titulo: (isExisitingHabilitationTitle(formData?.habilitationTitle) ? formData?.habilitationTitle : formData?.otherHabilitationTitle) || '',
                matricula: formData.licenceNumber,
                entidadOtorgante: formData.grantingEntity,
                idCargo: Number(formData.charge),
                idTipoContratacion: Number(formData.status),
                idRepresentacion: Number(formData.respresentation),
                idTipoProfesional: 1, //THIS IS HARCODED TO PREVENT API ERROR UNTIL UPDATE
                cantidadHoras: Number(formData.hoursQuantity)
            };
            const response: any = await editResponsible({
                reportId: selectedCarcinogenicReportId,
                responsibleId: Number(formData.id),
                body: adaptedBody
            });
            if (response?.error) {
                enqueueSnackbar(translate('enqueues.editResponsible.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(translate('enqueues.editResponsible.success'), {
                    variant: 'success'
                });
                handleCloseDrawer();
            }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo actualizar el responsable',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleCreateNewResponsible = async () => {
        try {
            if (!selectedCarcinogenicReportId) throw new Error('No report selected');

            const adaptedBody = {
                nombre: formData.name,
                apellido: formData.surname,
                cuit: formData.cuit,
                titulo: (isExisitingHabilitationTitle(formData?.habilitationTitle) ? formData?.habilitationTitle : formData?.otherHabilitationTitle) || '',
                matricula: formData.licenceNumber,
                entidadOtorgante: formData.grantingEntity,
                idCargo: Number(formData.charge),
                idTipoContratacion: Number(formData.status),
                idRepresentacion: Number(formData.respresentation),
                idTipoProfesional: 1, //THIS IS HARCODED TO PREVENT API ERROR UNTIL UPDATE
                cantidadHoras: Number(formData.hoursQuantity)
            };
            const response: any = await addResponsible({
                reportId: selectedCarcinogenicReportId,
                body: adaptedBody
            });
            if (response?.error) {
                enqueueSnackbar(translate('enqueues.createResponsible.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(translate('enqueues.createResponsible.success'), {
                    variant: 'success'
                });
                handleCloseDrawer();
            }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo crear el responsable',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleSubmit = () => {
        if (!validateForm(formData, responsibleFieldsConfig)) return;

        if (
            debeAvisarResponsableDuplicado(
                responsibles,
                formData.charge,
                isEditing ? formData.id : undefined
            )
        ) {
            enqueueSnackbar(translate('enqueues.duplicateResponsibleType.warning'), {
                variant: 'warning'
            });
        }

        if (isEditing) {
            showConfirmEditResponsibleAlert({ confirmAction: handleEditNewResponsible });
        } else {
            showConfirmCreateResponsibleAlert({ confirmAction: handleCreateNewResponsible });
        }
    };

    const resetForm = () => {
        setFormData(initialResponsibleForm);
    };

    return {
        formData,
        errors,
        responsibleFieldsConfig,
        isAddingResponsible,
        isEditingResponsible,
        translate,
        resetForm,
        handleChange,
        validateForm,
        handleSubmit
    };
};

export default useResponsibleForm;
